import React, { useState } from "react";
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, IconButton, Stack, Switch, TextField,
  Tooltip, Typography, Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  adminAddUserAddress, adminUpdateUserAddress, adminDeleteUserAddress,
} from "../api/superAdmin";
import { formatPhoneInput } from "../utils/phone";

interface Address {
  id: number;
  label?: string;
  full_name?: string;
  phone?: string;
  email?: string;
  address: string; // "street\napartment"
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  is_default?: boolean;
}

interface FormState {
  label: string;
  full_name: string;
  phone: string;
  email: string;
  street: string;
  unit: string; // apt/suite/unit — 2nd address line
  city: string;
  state: string;
  zip_code: string;
  country: string;
  is_default: boolean;
}

const emptyForm: FormState = {
  label: "", full_name: "", phone: "", email: "", street: "", unit: "",
  city: "", state: "", zip_code: "", country: "CA", is_default: false,
};

// The street + unit are stored as a single "street\nunit" address field.
const splitAddress = (addr: string) => {
  const nl = (addr || "").indexOf("\n");
  return nl >= 0
    ? { street: addr.slice(0, nl), unit: addr.slice(nl + 1) }
    : { street: addr || "", unit: "" };
};
const composeAddress = (street: string, unit: string) =>
  unit.trim() ? `${street.trim()}\n${unit.trim()}` : street.trim();

export default function CustomerAddressEditor({
  userId, addresses, onChanged,
}: {
  userId: number;
  addresses: Address[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setOpen(true);
  };

  const openEdit = (a: Address) => {
    const { street, unit } = splitAddress(a.address);
    setEditingId(a.id);
    setForm({
      label: a.label || "", full_name: a.full_name || "", phone: a.phone || "",
      email: a.email || "", street, unit, city: a.city || "", state: a.state || "",
      zip_code: a.zip_code || "", country: a.country || "CA", is_default: !!a.is_default,
    });
    setError(null);
    setOpen(true);
  };

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.street.trim()) {
      setError("Street address is required.");
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      label: form.label.trim() || undefined,
      full_name: form.full_name.trim() || undefined,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      address: composeAddress(form.street, form.unit),
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      zip_code: form.zip_code.trim() || undefined,
      country: form.country.trim() || "CA",
      is_default: form.is_default,
    };
    try {
      if (editingId) await adminUpdateUserAddress(userId, editingId, payload);
      else await adminAddUserAddress(userId, payload);
      setOpen(false);
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to save address.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await adminDeleteUserAddress(userId, id);
      onChanged();
    } catch {
      /* no-op; list stays as-is */
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {addresses.length} saved {addresses.length === 1 ? "address" : "addresses"}
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openAdd}>
          Add address
        </Button>
      </Stack>

      {addresses.map((a) => {
        const { street, unit } = splitAddress(a.address);
        return (
          <Card key={a.id} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 }, display: "flex", alignItems: "flex-start" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                  <Typography variant="body2" fontWeight={700}>{a.label || "Address"}</Typography>
                  {a.is_default && <Chip size="small" label="Default" color="primary" sx={{ height: 18 }} />}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  {street}{unit ? `\n${unit}` : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[a.city, a.state, a.zip_code].filter(Boolean).join(", ")}
                </Typography>
              </Box>
              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(a.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Edit address" : "Add address"}</DialogTitle>
        <DialogContent>
          {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Label (e.g. Home)" value={form.label} onChange={set("label")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Full name" value={form.full_name} onChange={set("full_name")} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone" value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: formatPhoneInput(e.target.value) }))}
                inputProps={{ inputMode: "tel" }} />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" value={form.email} onChange={set("email")} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" required label="Street address" value={form.street} onChange={set("street")} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Apartment, suite, unit (optional)" value={form.unit} onChange={set("unit")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="City" value={form.city} onChange={set("city")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Province" value={form.state} onChange={set("state")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Postal" value={form.zip_code} onChange={set("zip_code")} /></Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} />}
                label="Default address"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editingId ? "Save changes" : "Add address"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
